import { normalizeOutboundPhoneAddress } from './communicationsCompose';
import { formatPhone } from './phone';

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function firstName(value = '') {
    return firstString(value).split(/\s+/)[0] || '';
}

function checklistValue(session = {}, key = '') {
    const entry = session?.capture_checklist?.[key];
    if (typeof entry === 'string') return entry.trim();
    if (entry && typeof entry === 'object' && typeof entry.value === 'string') return entry.value.trim();
    return '';
}

function formatLocalTime(value, timezone) {
    const raw = firstString(value);
    if (!raw) return '';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
    }).format(parsed);
}

function formatLocalDate(value, timezone) {
    const raw = firstString(value);
    if (!raw) return '';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(parsed);
}

// A phone read by the person getting the message: +1 (404) 555-1234 (BL-177 Step 2d), never raw E.164.
function formatE164(value) {
    const normalized = normalizeOutboundPhoneAddress(value);
    return normalized.startsWith('+') ? formatPhone(normalized) : firstString(value);
}

// Comms final pass (1.12.217-beta): values every conversation knows without a trip. The Communications screen tells the
// resolver who is signed in, which company (brand) the open conversation belongs to, the company's time zone and the greeting
// cut-offs (Settings > Communications > Group communications), so every template picker fills them the same way. A context
// passed in explicitly always wins over these defaults.
export const DEFAULT_GREETING_CUTOFFS = Object.freeze({ afternoon: 12, evening: 17 });
const runtimeDefaults = { staffName: '', companyName: '', timezone: '', greetingCutoffs: { ...DEFAULT_GREETING_CUTOFFS } };

export function setTemplateRuntimeDefaults(next = {}) {
    if (typeof next.staffName === 'string') runtimeDefaults.staffName = next.staffName;
    if (typeof next.companyName === 'string') runtimeDefaults.companyName = next.companyName;
    if (typeof next.timezone === 'string') runtimeDefaults.timezone = next.timezone;
    if (next.greetingCutoffs) runtimeDefaults.greetingCutoffs = normalizeGreetingCutoffs(next.greetingCutoffs);
}

export function getTemplateRuntimeDefaults() {
    return { ...runtimeDefaults, greetingCutoffs: { ...runtimeDefaults.greetingCutoffs } };
}

export function resetTemplateRuntimeDefaults() {
    runtimeDefaults.staffName = '';
    runtimeDefaults.companyName = '';
    runtimeDefaults.timezone = '';
    runtimeDefaults.greetingCutoffs = { ...DEFAULT_GREETING_CUTOFFS };
}

export function normalizeGreetingCutoffs(value = {}) {
    const afternoon = Number.parseInt(value?.afternoon, 10);
    const evening = Number.parseInt(value?.evening, 10);
    if (Number.isFinite(afternoon) && Number.isFinite(evening) && afternoon >= 1 && evening <= 23 && afternoon < evening) {
        return { afternoon, evening };
    }
    return { ...DEFAULT_GREETING_CUTOFFS };
}

/** The hour (0-23) on the wall clock in `timezone` at `now`. An unknown zone falls back to America/New_York. */
export function hourInTimeZone(now = new Date(), timezone = 'America/New_York') {
    const at = now instanceof Date ? now : new Date(now);
    if (Number.isNaN(at.getTime())) return null;
    const read = (zone) => new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', hourCycle: 'h23' })
        .formatToParts(at).find((part) => part.type === 'hour')?.value;
    let hour;
    try { hour = read(timezone || 'America/New_York'); } catch { hour = read('America/New_York'); }
    const parsed = Number.parseInt(hour, 10);
    return Number.isFinite(parsed) ? parsed % 24 : null;
}

/** "Good morning" before the afternoon cut-off, "Good afternoon" until the evening one, then "Good evening". */
export function timeOfDayGreeting(now = new Date(), timezone = 'America/New_York', cutoffs = DEFAULT_GREETING_CUTOFFS) {
    const hour = hourInTimeZone(now, timezone);
    if (hour === null) return '';
    const { afternoon, evening } = normalizeGreetingCutoffs(cutoffs);
    if (hour < afternoon) return 'Good morning';
    if (hour < evening) return 'Good afternoon';
    return 'Good evening';
}

function resolveBySource(source = 'manual', {
    session = null,
    contact = null,
    contactName = '',
    trip = null,
    engagement = null,
    flight = null,
    assignment = null,
    driver = null,
    vehicle = null,
    timezone = 'America/New_York',
    staffName = '',
    companyName = '',
    greetingCutoffs = null,
    now = null,
} = {}) {
    const resolvedContactName = firstString(
        contact?.full_name,
        contact?.name,
        contactName,
        checklistValue(session, 'full_name')
    );
    switch (source) {
    case 'contact.first_name':
        return firstName(resolvedContactName);
    case 'contact.full_name':
        return resolvedContactName;
    case 'trip.pickup_time':
        return formatLocalTime(
            firstString(
                checklistValue(session, 'time'),
                trip?.pickup_time,
                trip?.scheduled_at,
                engagement?.scheduled_at
            ),
            timezone
        );
    case 'trip.pickup_date':
        return formatLocalDate(
            firstString(
                checklistValue(session, 'date'),
                trip?.pickup_date,
                trip?.scheduled_at,
                engagement?.scheduled_at
            ),
            timezone
        );
    case 'trip.pickup_location':
        return firstString(
            checklistValue(session, 'pickup_location'),
            trip?.pickup_location,
            trip?.pickup_address,
            engagement?.pickup_address
        );
    case 'trip.dropoff_location':
        return firstString(
            checklistValue(session, 'dropoff_location'),
            trip?.dropoff_location,
            trip?.dropoff_address,
            engagement?.dropoff_address
        );
    case 'trip.flight_number':
        return firstString(
            checklistValue(session, 'flight_info'),
            trip?.flight_number,
            flight?.flight_number,
            trip?.flight_info
        );
    case 'driver.name':
        return firstString(driver?.full_name, driver?.name, assignment?.driver_name, trip?.driver_name, trip?.driver);
    case 'driver.phone':
        return formatE164(firstString(driver?.phone, driver?.phone_number, assignment?.driver_phone, trip?.driver_phone));
    case 'vehicle.vehicle_class':
    case 'vehicle.description':
        return firstString(vehicle?.vehicle_class, trip?.vehicle_class, assignment?.vehicle_class, trip?.vehicle);
    case 'vehicle.plate':
        return firstString(vehicle?.plate, assignment?.vehicle_plate, trip?.plate);
    case 'greeting.time_of_day':
        return timeOfDayGreeting(now || new Date(), timezone, greetingCutoffs || runtimeDefaults.greetingCutoffs);
    case 'staff.first_name':
        return firstName(firstString(staffName, runtimeDefaults.staffName));
    case 'company.name':
        return firstString(companyName, runtimeDefaults.companyName);
    case 'trip.confirmation_number':
        return firstString(trip?.confirmation_number, engagement?.confirmation_number, assignment?.confirmation_number, session?.confirmation_number);
    default:
        return '';
    }
}

// Pickup ETA / "minutes away" is deliberately not offered: no reliable per-trip ETA exists in the data yet (comms final pass,
// Docs/Open_Questions_Comms_Final_Pass.md).
export const TEMPLATE_FIELD_SOURCE_HELP = 'Filled in automatically when the template is inserted. If the value can\'t be found, staff are asked to type it.';

export const TEMPLATE_FIELD_SOURCE_OPTIONS = [
    { value: 'manual', label: 'Typed by staff' },
    { value: 'contact.first_name', label: 'Customer first name' },
    { value: 'contact.full_name', label: 'Customer full name' },
    { value: 'greeting.time_of_day', label: 'Time-of-day greeting (Good morning / afternoon / evening)' },
    { value: 'staff.first_name', label: 'Your first name (the staff member sending)' },
    { value: 'company.name', label: 'Company name (the conversation\'s brand)' },
    { value: 'trip.pickup_time', label: 'Pickup time' },
    { value: 'trip.pickup_date', label: 'Pickup date' },
    { value: 'trip.pickup_location', label: 'Pickup location' },
    { value: 'trip.dropoff_location', label: 'Drop-off location' },
    { value: 'trip.flight_number', label: 'Flight number' },
    { value: 'driver.name', label: 'Driver name' },
    { value: 'driver.phone', label: 'Driver phone' },
    { value: 'vehicle.vehicle_class', label: 'Vehicle type' },
    { value: 'vehicle.plate', label: 'Plate' },
    { value: 'trip.confirmation_number', label: 'Confirmation number' },
];

/**
 * Comms gate fixes: the zone used only when the company's own time zone is not known (no AI assistant settings row yet). It is a
 * named, explicit fallback, never mistaken for a zone somebody chose: a caller that passes 'America/New_York' on purpose gets it.
 */
export const COMPANY_TIMEZONE_UNKNOWN_FALLBACK = 'America/New_York';

/** The zone a template is filled in: an explicit context zone (any zone, Eastern included), then the conversation's, then the
 * company's (set by the Communications screen), then the "company time zone unknown" fallback. */
export function templateTimeZone(context = {}) {
    return firstString(context?.timezone, context?.session?.timezone, runtimeDefaults.timezone) || COMPANY_TIMEZONE_UNKNOWN_FALLBACK;
}

export function resolveTemplateFieldValues(templateVariables = {}, context = {}) {
    const values = {};
    const missing = [];
    const timezone = templateTimeZone(context);

    Object.entries(templateVariables || {}).forEach(([key, config]) => {
        const source = typeof config === 'object' && config !== null ? (config.source || 'manual') : 'manual';
        const value = resolveBySource(source, { ...context, timezone });
        values[key] = value;
        if (!firstString(value)) missing.push(key);
    });

    return { values, missing };
}

const AUTO_GREETINGS = new Set(['', 'Good morning', 'Good afternoon', 'Good evening']);

/**
 * Comms gate fixes: a scheduled message's time-of-day greeting must suit the moment it is DELIVERED, not the moment it was
 * written. The greeting depends only on the delivery instant (in the company's zone, with the company's cut-offs), so it is
 * resolved for the scheduled time when the message is scheduled. A value staff typed themselves (not one of the three automatic
 * greetings) is left alone. Pure; returns a new values object.
 */
export function valuesForDeliveryTime(template, values = {}, { at, timezone = '', greetingCutoffs = null } = {}) {
    const next = { ...(values || {}) };
    const when = at instanceof Date ? at : new Date(at);
    if (Number.isNaN(when.getTime())) return next;
    const zone = templateTimeZone({ timezone });
    Object.entries(template?.variables || {}).forEach(([key, config]) => {
        if (config && typeof config === 'object' && config.source === 'greeting.time_of_day'
            && AUTO_GREETINGS.has(String(next[key] ?? '').trim())) {
            next[key] = timeOfDayGreeting(when, zone, greetingCutoffs || runtimeDefaults.greetingCutoffs);
        }
    });
    return next;
}

/** Comms gate fixes: the brand a template's "Company name" fills with, for an entity (walks up to the nearest brand). Pure. */
export function brandNameForEntity(entities = [], entityId = '') {
    const byId = new Map((Array.isArray(entities) ? entities : []).map((entity) => [entity?.id, entity]));
    let entity = byId.get(entityId || '') || null;
    for (let hops = 0; entity && hops < 8; hops += 1) {
        if (firstString(entity.brand_name)) return entity.brand_name.trim();
        entity = byId.get(entity.parent_entity_id) || null;
    }
    return '';
}

/** Comms gate fixes: re-point every "Company name" value of a template at `companyName` (when the brand changes). Pure. */
export function withCompanyName(template, values = {}, companyName = '') {
    const next = { ...(values || {}) };
    Object.entries(template?.variables || {}).forEach(([key, config]) => {
        if (config && typeof config === 'object' && config.source === 'company.name') next[key] = companyName || '';
    });
    return next;
}
